import React from 'react';
import { FiLock, FiUnlock } from 'react-icons/fi';
import { useTranslation } from '../contexts/TranslationContext';
import { BetEvent, BetResult } from '../types/interfaces';
import { Icon } from '../utils/Icon';
import { translateEvent } from '../utils/translateEvent';

interface BetEventPanelProps {
  betEvent: BetEvent;
  isLocked: boolean;
  onLockToggle: (eventId: number) => void;
  disabled?: boolean;
  showLegResult?: boolean;
}

const BetEventPanel: React.FC<BetEventPanelProps> = ({
  betEvent,
  isLocked,
  onLockToggle,
  disabled = false,
  showLegResult = false,
}) => {
  const { t } = useTranslation();
  const lr = t.myParlays?.legResult;

  const legResultKind = (r: BetResult | null | undefined): 'win' | 'lost' | 'void' | 'pending' => {
    if (r === 'WIN') return 'win';
    if (r === 'LOOSE') return 'lost';
    if (r === 'VOID') return 'void';
    return 'pending';
  };

  const legResultLabel = (r: BetResult | null | undefined): string => {
    if (r === 'WIN') return lr.win;
    if (r === 'LOOSE') return lr.lost;
    if (r === 'VOID') return lr.void;
    if (r === 'TO_RESOLVE' || r === 'UNKNOWN') return lr.pending;
    return lr.pending;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className={`bet-event-panel${isLocked ? ' bet-event-panel--locked' : ''}`}>
      <div className="bet-event-panel__content">
        <div className="bet-event-panel__header">
          <h3 className="bet-event-panel__teams">
            {translateEvent(betEvent.game?.home_team ?? '', t.eventsDictionary)} - {translateEvent(betEvent.game?.away_team ?? '', t.eventsDictionary)}
          </h3>
          <p className="bet-event-panel__event">{translateEvent(betEvent.event, t.eventsDictionary)}</p>
        </div>

        <div className="bet-event-panel__footer">
          <div className="bet-event-panel__odds">
            <span className="bet-event-panel__odds-value">{betEvent.odds}</span>
          </div>

          {showLegResult && (
            <span
              className={`bet-event-panel__leg-result bet-event-panel__leg-result--${legResultKind(betEvent.result)}`}
            >
              {legResultLabel(betEvent.result)}
            </span>
          )}

          <div className="bet-event-panel__meta">
            <p className="bet-event-panel__league">
              {betEvent.game?.league?.name ? translateEvent(betEvent.game.league.name, t.eventsDictionary) : t.betEventPanel.leagueFallback.replace('{id}', String(betEvent.game?.league_id ?? ''))}
            </p>
            <p className="bet-event-panel__date">
              {betEvent.game?.datetime ? formatDate(betEvent.game.datetime) : t.betEventPanel.tbd}
            </p>
          </div>

          {!disabled && (
            <button
              className={`bet-event-panel__lock-button${isLocked ? ' bet-event-panel__lock-button--locked' : ''}`}
              onClick={() => onLockToggle(betEvent.id)}
              aria-label={isLocked ? t.betEventPanel.unlock : t.betEventPanel.lock}
            >
              {isLocked ? <Icon component={FiLock} aria-hidden={true} /> : <Icon component={FiUnlock} aria-hidden={true} />}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BetEventPanel;
